import React, { useEffect, useMemo, useState, useCallback } from "react";
import api from "../lib/api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Label } from "./ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "./ui/dialog";
import { toast } from "sonner";
import { Plus, Trash2, GripVertical, ChevronDown, ChevronRight, Save, X, Snowflake } from "lucide-react";

const OWNER_ROLES = ["admin", "team", "account_manager", "client"];
const uid = () => (crypto?.randomUUID?.() || Math.random().toString(36).slice(2));

function offsetHint(eventDate, offset) {
  if (!eventDate || offset === "" || offset == null) return null;
  try {
    const d = new Date(eventDate);
    d.setDate(d.getDate() + parseInt(offset, 10));
    return d.toLocaleDateString(undefined, { day: "numeric", month: "short" });
  } catch { return null; }
}

/** Shared editor for both live engagements and templates.
 *  props: mode = 'engagement' | 'template', entityId, open, onOpenChange, onSaved
 */
export default function EngagementEditorDialog({ mode, entityId, open, onOpenChange, onSaved }) {
  const isTemplate = mode === "template";
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [record, setRecord] = useState(null);      // untouched original
  const [draft, setDraft] = useState(null);        // editable working copy
  const [phaseOpen, setPhaseOpen] = useState(false);

  const load = useCallback(async () => {
    if (!open || !entityId) return;
    setLoading(true);
    try {
      const url = isTemplate ? `/engagement-templates/${entityId}` : `/engagements/${entityId}`;
      const r = await api.get(url);
      setRecord(r.data);
      // Deep clone for draft
      setDraft(JSON.parse(JSON.stringify(r.data)));
    } catch (e) { toast.error(e?.response?.data?.detail || "Load failed"); }
    finally { setLoading(false); }
  }, [open, entityId, isTemplate]);
  useEffect(() => { load(); }, [load]);

  const dirty = useMemo(() => JSON.stringify(record) !== JSON.stringify(draft), [record, draft]);

  const closeAsking = () => {
    if (dirty && !window.confirm("Discard unsaved changes?")) return;
    onOpenChange(false);
  };

  const errors = useMemo(() => {
    if (!draft) return {};
    const out = {};
    const phaseIds = new Set((draft.phases || []).map((p) => p.id));
    const checkItem = (it, prefix) => {
      if (!it.title || !it.title.trim()) out[`${prefix}-${it.id}-title`] = "required";
      const o = it.offset_days;
      if (o !== 0 && !o && o !== "0") out[`${prefix}-${it.id}-offset`] = "required";
      if (Number.isNaN(parseInt(o, 10))) out[`${prefix}-${it.id}-offset`] = "must be integer";
      if (it.phase_id && !phaseIds.has(it.phase_id)) out[`${prefix}-${it.id}-phase`] = "phase missing";
    };
    (draft.deliverables || []).forEach((d) => checkItem(d, "d"));
    (draft.meetings || []).forEach((m) => checkItem(m, "m"));
    (draft.payment_milestones || []).forEach((m) => {
      if (!m.label || !m.label.trim()) out[`pm-${m.id}-label`] = "required";
      if (Number.isNaN(parseInt(m.due_offset_days, 10))) out[`pm-${m.id}-offset`] = "must be integer";
    });
    (draft.phases || []).forEach((p) => {
      if (!p.name || !p.name.trim()) out[`ph-${p.id}-name`] = "required";
    });
    return out;
  }, [draft]);

  const hasErrors = Object.keys(errors).length > 0;

  const save = async () => {
    if (!draft || hasErrors) return;
    setBusy(true);
    try {
      const url = isTemplate ? `/engagement-templates/${entityId}` : `/engagements/${entityId}`;
      const body = {
        ...(isTemplate ? { name: draft.name, length_days: draft.length_days } : {}),
        phases: draft.phases,
        deliverables: draft.deliverables,
        meetings: draft.meetings,
        payment_milestones: draft.payment_milestones,
      };
      await api.put(url, body);
      toast.success("Saved");
      onSaved?.();
      onOpenChange(false);
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    finally { setBusy(false); }
  };

  if (!open) return null;

  return (
    <Dialog open={open} onOpenChange={closeAsking}>
      <DialogContent className="max-w-5xl bg-[#0a0a0a] border-zinc-800 max-h-[92vh] overflow-hidden flex flex-col p-0" data-testid="engagement-editor">
        <DialogHeader className="p-5 border-b border-zinc-900 flex-row items-center justify-between">
          <div>
            <DialogTitle className="font-display text-zinc-100 flex items-center gap-2">
              {isTemplate ? "Edit template" : "Edit engagement"}
              {draft && <span className="text-zinc-500 text-[12px] font-mono">· {draft.name}</span>}
              {dirty && <span className="text-amber-400 text-[10px] uppercase tracking-wider">unsaved</span>}
            </DialogTitle>
            <DialogDescription className="text-[11px] text-zinc-500">
              {isTemplate ? "Changes only affect this template; live engagements aren't touched." : "Completions stay attached to existing rows by id."}
            </DialogDescription>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="ghost" size="sm" onClick={closeAsking} className="h-8 text-[12px]" data-testid="editor-discard-btn">
              <X className="w-3 h-3 mr-1" /> Discard
            </Button>
            <Button onClick={save} disabled={busy || !dirty || hasErrors}
              className="bg-white text-black hover:bg-zinc-200 h-8 text-[12px]" data-testid="editor-save-btn">
              <Save className="w-3 h-3 mr-1" />{busy ? "Saving…" : "Save"}
            </Button>
          </div>
        </DialogHeader>

        {loading || !draft ? (
          <div className="p-6 text-zinc-500 text-[12px]">Loading…</div>
        ) : (
          <div className="p-5 space-y-6 overflow-y-auto flex-1">

            {/* Phases (collapsed by default) */}
            <div className="tt-card p-3">
              <button onClick={() => setPhaseOpen((v) => !v)} className="flex items-center gap-2 text-zinc-300"
                data-testid="editor-phases-toggle">
                {phaseOpen ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}
                <span className="t-eyebrow">Phases</span>
                <span className="text-[10px] text-zinc-500 font-mono">({(draft.phases || []).length})</span>
              </button>
              {phaseOpen && (
                <div className="mt-3 space-y-2" data-testid="editor-phases-list">
                  <ItemList
                    kind="phase"
                    items={draft.phases}
                    onChange={(items) => setDraft({ ...draft, phases: items })}
                    errors={errors}
                  />
                  <button className="text-[11px] text-zinc-400 hover:text-white flex items-center gap-1"
                    onClick={() => setDraft({ ...draft, phases: [...(draft.phases || []), { id: uid(), name: "New phase", start_offset: 0, end_offset: 0, order: (draft.phases || []).length }] })}
                    data-testid="editor-add-phase">
                    <Plus className="w-3 h-3" /> Add phase
                  </button>
                </div>
              )}
            </div>

            <Section
              title="Deliverables" testid="editor-deliverables-section"
              items={draft.deliverables || []} phases={draft.phases || []}
              eventDate={!isTemplate ? draft.event_date : null}
              onChange={(items) => setDraft({ ...draft, deliverables: items })}
              errors={errors} prefix="d"
              newItem={() => ({ id: uid(), title: "", offset_days: 0, phase_id: (draft.phases?.[0]?.id) || null, owner_role: "team", done: false })}
            />

            <Section
              title="Meetings" testid="editor-meetings-section"
              items={draft.meetings || []} phases={draft.phases || []}
              eventDate={!isTemplate ? draft.event_date : null}
              onChange={(items) => setDraft({ ...draft, meetings: items })}
              errors={errors} prefix="m"
              newItem={() => ({ id: uid(), title: "", offset_days: 0, phase_id: (draft.phases?.[0]?.id) || null, owner_role: "am", done: false })}
            />

            <MilestoneSection
              items={draft.payment_milestones || []}
              eventDate={!isTemplate ? draft.event_date : null}
              onChange={(items) => setDraft({ ...draft, payment_milestones: items })}
              errors={errors}
            />
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

// ── Section: Deliverables OR Meetings ────────────────────────────────────
function Section({ title, testid, items, phases, eventDate, onChange, errors, prefix, newItem }) {
  const addRow = () => {
    const next = [...items, newItem()];
    onChange(next);
  };
  const patch = (id, p) => onChange(items.map((it) => it.id === id ? { ...it, ...p } : it));
  const remove = (it) => {
    if (it.done && !window.confirm("This is marked done. Delete anyway?")) return;
    onChange(items.filter((i) => i.id !== it.id));
  };
  const reorder = (fromIdx, toIdx) => {
    if (fromIdx === toIdx) return;
    const copy = items.slice();
    const [x] = copy.splice(fromIdx, 1);
    copy.splice(toIdx, 0, x);
    onChange(copy.map((it, i) => ({ ...it, order: i })));
  };
  return (
    <div className="tt-card p-3" data-testid={testid}>
      <div className="flex items-center justify-between mb-2">
        <div className="t-eyebrow">{title}</div>
        <span className="text-[10px] text-zinc-500 font-mono">{items.length}</span>
      </div>
      <div className="space-y-1.5">
        {items.map((it, idx) => (
          <Row key={it.id} it={it} idx={idx} phases={phases} eventDate={eventDate}
            onPatch={patch} onRemove={() => remove(it)} onReorder={reorder}
            errors={errors} prefix={prefix} />
        ))}
      </div>
      <button className="mt-2 text-[11px] text-zinc-400 hover:text-white flex items-center gap-1" onClick={addRow}
        data-testid={`${testid}-add`}>
        <Plus className="w-3 h-3" /> Add {title.toLowerCase().replace(/s$/, "")}
      </button>
    </div>
  );
}

function Row({ it, idx, phases, eventDate, onPatch, onRemove, onReorder, errors, prefix }) {
  const onDragStart = (e) => { e.dataTransfer.setData("text/plain", String(idx)); };
  const onDragOver  = (e) => e.preventDefault();
  const onDrop      = (e) => {
    e.preventDefault();
    const from = parseInt(e.dataTransfer.getData("text/plain"), 10);
    onReorder(from, idx);
  };
  const hint = offsetHint(eventDate, it.offset_days);
  return (
    <div className="grid grid-cols-12 gap-2 items-start" data-testid={`row-${prefix}-${it.id}`}
      draggable onDragStart={onDragStart} onDragOver={onDragOver} onDrop={onDrop}>
      <div className="col-span-1 flex items-center pt-1.5 text-zinc-600 cursor-grab" title="Drag to reorder">
        <GripVertical className="w-3.5 h-3.5" />
      </div>
      <div className="col-span-4">
        <Input value={it.title || ""} onChange={(e) => onPatch(it.id, { title: e.target.value })}
          placeholder="Title" data-testid={`row-${prefix}-${it.id}-title`}
          className={`bg-[#0a0a0a] border-zinc-800 h-8 text-[12px] ${errors[`${prefix}-${it.id}-title`] ? "border-red-500" : ""}`} />
        {errors[`${prefix}-${it.id}-title`] && <div className="text-[10px] text-red-400 mt-0.5">{errors[`${prefix}-${it.id}-title`]}</div>}
      </div>
      <div className="col-span-3">
        <select value={it.phase_id || ""} onChange={(e) => onPatch(it.id, { phase_id: e.target.value || null })}
          data-testid={`row-${prefix}-${it.id}-phase`}
          className="bg-[#111] border border-zinc-800 rounded h-8 text-[11px] px-2 w-full">
          <option value="">— no phase —</option>
          {phases.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
        </select>
      </div>
      <div className="col-span-2">
        <Input type="number" step="1" value={it.offset_days ?? ""}
          onChange={(e) => onPatch(it.id, { offset_days: e.target.value === "" ? "" : parseInt(e.target.value, 10) })}
          data-testid={`row-${prefix}-${it.id}-offset`}
          className={`bg-[#0a0a0a] border-zinc-800 h-8 text-[12px] ${errors[`${prefix}-${it.id}-offset`] ? "border-red-500" : ""}`} />
        {errors[`${prefix}-${it.id}-offset`] && <div className="text-[10px] text-red-400 mt-0.5">{errors[`${prefix}-${it.id}-offset`]}</div>}
      </div>
      <div className="col-span-1">
        <select value={it.owner_role || "team"} onChange={(e) => onPatch(it.id, { owner_role: e.target.value })}
          data-testid={`row-${prefix}-${it.id}-owner`}
          className="bg-[#111] border border-zinc-800 rounded h-8 text-[11px] px-2 w-full">
          {OWNER_ROLES.map((r) => <option key={r} value={r}>{r}</option>)}
        </select>
      </div>
      <div className="col-span-1 flex items-center justify-end pt-1">
        <button onClick={onRemove} className="text-zinc-500 hover:text-[#EF4444] h-7 w-7 flex items-center justify-center"
          data-testid={`row-${prefix}-${it.id}-remove`}>
          <Trash2 className="w-3 h-3" />
        </button>
      </div>
    </div>
  );
}

// ── Phase editing ────────────────────────────────────────────────────────
function ItemList({ items, onChange, errors, kind }) {
  const patch = (id, p) => onChange(items.map((it) => it.id === id ? { ...it, ...p } : it));
  const remove = (id) => onChange(items.filter((i) => i.id !== id));
  const reorder = (fromIdx, toIdx) => {
    if (fromIdx === toIdx) return;
    const copy = items.slice();
    const [x] = copy.splice(fromIdx, 1);
    copy.splice(toIdx, 0, x);
    onChange(copy.map((it, i) => ({ ...it, order: i })));
  };
  return (
    <div className="space-y-1.5">
      {items.map((p, idx) => (
        <div key={p.id} className="grid grid-cols-12 gap-2 items-start" data-testid={`row-ph-${p.id}`}
          draggable onDragStart={(e) => e.dataTransfer.setData("text/plain", String(idx))}
          onDragOver={(e) => e.preventDefault()} onDrop={(e) => { e.preventDefault(); const f = parseInt(e.dataTransfer.getData("text/plain"), 10); reorder(f, idx); }}>
          <div className="col-span-1 flex items-center pt-1.5 text-zinc-600 cursor-grab"><GripVertical className="w-3.5 h-3.5" /></div>
          <div className="col-span-5">
            <Input value={p.name || ""} onChange={(e) => patch(p.id, { name: e.target.value })}
              placeholder="Phase name" data-testid={`row-ph-${p.id}-name`}
              className={`bg-[#0a0a0a] border-zinc-800 h-8 text-[12px] ${errors[`ph-${p.id}-name`] ? "border-red-500" : ""}`} />
          </div>
          <div className="col-span-2">
            <Input type="number" value={p.start_offset ?? 0} onChange={(e) => patch(p.id, { start_offset: parseInt(e.target.value || "0", 10) })}
              placeholder="start" data-testid={`row-ph-${p.id}-start`}
              className="bg-[#0a0a0a] border-zinc-800 h-8 text-[12px]" />
          </div>
          <div className="col-span-2">
            <Input type="number" value={p.end_offset ?? 0} onChange={(e) => patch(p.id, { end_offset: parseInt(e.target.value || "0", 10) })}
              placeholder="end" data-testid={`row-ph-${p.id}-end`}
              className="bg-[#0a0a0a] border-zinc-800 h-8 text-[12px]" />
          </div>
          <div className="col-span-2 flex items-center justify-end pt-1">
            <button onClick={() => remove(p.id)} className="text-zinc-500 hover:text-[#EF4444] h-7 w-7 flex items-center justify-center"
              data-testid={`row-ph-${p.id}-remove`}>
              <Trash2 className="w-3 h-3" />
            </button>
          </div>
        </div>
      ))}
    </div>
  );
}

// ── Milestones ───────────────────────────────────────────────────────────
function MilestoneSection({ items, eventDate, onChange, errors }) {
  const add = () => onChange([...items, { id: uid(), seq: items.length + 1, label: "New milestone", trigger: "", amount: 0, due_offset_days: 0, status: "scheduled" }]);
  const patch = (id, p) => onChange(items.map((it) => it.id === id ? { ...it, ...p } : it));
  const remove = (id) => onChange(items.filter((i) => i.id !== id));
  const reorder = (fromIdx, toIdx) => {
    if (fromIdx === toIdx) return;
    const copy = items.slice();
    const [x] = copy.splice(fromIdx, 1);
    copy.splice(toIdx, 0, x);
    onChange(copy.map((it, i) => ({ ...it, seq: i + 1 })));
  };
  return (
    <div className="tt-card p-3" data-testid="editor-milestones-section">
      <div className="flex items-center justify-between mb-2">
        <div className="t-eyebrow">Payment milestones</div>
        <span className="text-[10px] text-zinc-500 font-mono">{items.length}</span>
      </div>
      <div className="space-y-1.5">
        {items.map((m, idx) => {
          const hint = offsetHint(eventDate, m.due_offset_days);
          return (
            <div key={m.id} className="grid grid-cols-12 gap-2 items-start" data-testid={`row-pm-${m.id}`}
              draggable onDragStart={(e) => e.dataTransfer.setData("text/plain", String(idx))}
              onDragOver={(e) => e.preventDefault()} onDrop={(e) => { e.preventDefault(); const f = parseInt(e.dataTransfer.getData("text/plain"), 10); reorder(f, idx); }}>
              <div className="col-span-1 flex items-center pt-1.5 text-zinc-600 cursor-grab"><GripVertical className="w-3.5 h-3.5" /></div>
              <div className="col-span-3">
                <Input value={m.label || ""} onChange={(e) => patch(m.id, { label: e.target.value })}
                  placeholder="Label" data-testid={`row-pm-${m.id}-label`}
                  className={`bg-[#0a0a0a] border-zinc-800 h-8 text-[12px] ${errors[`pm-${m.id}-label`] ? "border-red-500" : ""}`} />
              </div>
              <div className="col-span-3">
                <Input value={m.trigger || ""} onChange={(e) => patch(m.id, { trigger: e.target.value })}
                  placeholder="Trigger (e.g. On sign-off)" className="bg-[#0a0a0a] border-zinc-800 h-8 text-[12px]" />
              </div>
              <div className="col-span-2">
                <Input type="number" step="0.01" value={m.amount ?? ""} onChange={(e) => patch(m.id, { amount: e.target.value === "" ? null : Number(e.target.value) })}
                  placeholder="Amount (₹)" data-testid={`row-pm-${m.id}-amount`} className="bg-[#0a0a0a] border-zinc-800 h-8 text-[12px]" />
              </div>
              <div className="col-span-2">
                <Input type="number" value={m.due_offset_days ?? 0}
                  onChange={(e) => patch(m.id, { due_offset_days: e.target.value === "" ? 0 : parseInt(e.target.value, 10) })}
                  placeholder="offset" data-testid={`row-pm-${m.id}-offset`}
                  className={`bg-[#0a0a0a] border-zinc-800 h-8 text-[12px] ${errors[`pm-${m.id}-offset`] ? "border-red-500" : ""}`} />
                {hint && <div className="text-[10px] text-zinc-600 mt-0.5">{hint}</div>}
              </div>
              <div className="col-span-1 flex items-center justify-end pt-1">
                <button onClick={() => remove(m.id)} className="text-zinc-500 hover:text-[#EF4444] h-7 w-7 flex items-center justify-center"
                  data-testid={`row-pm-${m.id}-remove`}>
                  <Trash2 className="w-3 h-3" />
                </button>
              </div>
            </div>
          );
        })}
      </div>
      <button className="mt-2 text-[11px] text-zinc-400 hover:text-white flex items-center gap-1" onClick={add}
        data-testid="editor-milestones-section-add">
        <Plus className="w-3 h-3" /> Add milestone
      </button>
    </div>
  );
}


// ── Freeze dialog (also lives in this file — same interaction pattern) ───
export function FreezeDialog({ open, onOpenChange, engagement, milestones, onFrozen }) {
  const [reason, setReason] = useState("");
  const [message, setMessage] = useState(
    "This dashboard is temporarily paused pending a scheduled payment. " +
    "Please reach out to your account manager to resume access."
  );
  const [milestoneId, setMilestoneId] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) { setReason(""); setMilestoneId(""); } }, [open]);
  const submit = async () => {
    if (!reason.trim()) { toast.error("Reason is required"); return; }
    setBusy(true);
    try {
      await api.post(`/engagements/${engagement.id}/freeze`, {
        reason: reason.trim(),
        message: message.trim() || null,
        linked_milestone_id: milestoneId || null,
      });
      toast.success("Frozen");
      onFrozen?.();
      onOpenChange(false);
    } catch (e) { toast.error(e?.response?.data?.detail || "Freeze failed"); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md bg-[#0a0a0a] border-zinc-800" data-testid="freeze-dialog">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2"><Snowflake className="w-4 h-4 text-amber-400" /> Freeze engagement</DialogTitle>
          <DialogDescription className="text-[11px] text-zinc-500">Replaces the client's dashboard until you unfreeze.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="text-[10px] uppercase tracking-wider text-zinc-500 font-mono">Reason (required)</Label>
            <Input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="e.g. Q3 payment overdue"
              className="bg-[#0a0a0a] border-zinc-800 h-8 text-[12px] mt-1" data-testid="freeze-reason" />
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-wider text-zinc-500 font-mono">Message shown to the client</Label>
            <Textarea value={message} onChange={(e) => setMessage(e.target.value)} rows={4}
              className="bg-[#0a0a0a] border-zinc-800 text-[12px] mt-1" data-testid="freeze-message" />
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-wider text-zinc-500 font-mono">Linked payment milestone (optional)</Label>
            <select value={milestoneId} onChange={(e) => setMilestoneId(e.target.value)}
              className="bg-[#111] border border-zinc-800 rounded h-8 text-[12px] px-2 w-full mt-1"
              data-testid="freeze-milestone">
              <option value="">— none —</option>
              {(milestones || []).map((m) => <option key={m.id} value={m.id}>#{m.seq} · {m.label}</option>)}
            </select>
          </div>
        </div>
        <div className="flex items-center justify-end gap-2 pt-2">
          <Button variant="ghost" size="sm" onClick={() => onOpenChange(false)} className="h-8 text-[12px]">Cancel</Button>
          <Button onClick={submit} disabled={busy} className="bg-white text-black hover:bg-zinc-200 h-8 text-[12px]" data-testid="freeze-submit">
            <Snowflake className="w-3 h-3 mr-1" />{busy ? "Freezing…" : "Freeze"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
